import * as LabelPrimitive from "@radix-ui/react-label";
import { Slot } from "@radix-ui/react-slot";
import * as React from "react";
import {
    Controller,
    ControllerProps,
    FieldPath,
    FieldValues,
    FormProvider,
    useFormContext,
} from "react-hook-form";

import { constantsData } from "@/data";
import { generateValidClassNameHandler } from "@/lib/utils";
import { Label } from "@/ui/label";

const Form = FormProvider;

type FormFieldContextValue<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
> = {
  name: TName
}

const FormFieldContext = React.createContext<FormFieldContextValue>(
  {} as FormFieldContextValue,
);

const FormField = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
>({
        ...props
    }: ControllerProps<TFieldValues, TName>) => (
        <FormFieldContext.Provider value={{ name: props.name }}> {/* eslint-disable-line */}
            <Controller {...props} />
        </FormFieldContext.Provider>
    );

const useFormField = () => {
    const fieldContext = React.useContext(FormFieldContext);
    const itemContext = React.useContext(FormItemContext); // eslint-disable-line
    const {
        formState,
        getFieldState,
    } = useFormContext();

    const fieldState = getFieldState(
        fieldContext.name,
        formState,
    );

    if (!fieldContext) throw new Error(constantsData.msgs.providers.formField);

    const { id } = itemContext;

    return {
        ...fieldState,
        formDescriptionId: `${fieldContext.name}-form-item-description`,
        formItemId: `${fieldContext.name}-form-item`,
        formMessageId: `${fieldContext.name}-form-item-message`,
        id,
        name: fieldContext.name,
    };
};

type FormItemContextValue = {
  id: string
}

const FormItemContext = React.createContext<FormItemContextValue>(
  {} as FormItemContextValue,
);

const FormItem = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({
    className,
    ...props
}, ref) => {
    const id = React.useId();

    return (
        <FormItemContext.Provider value={{ id }}> {/* eslint-disable-line */}
            <div
                ref={ref}
                className={generateValidClassNameHandler(
                    "space-y-2",
                    className,
                )}
                {...props}
            />
        </FormItemContext.Provider>
    );
});
FormItem.displayName = "FormItem";

const FormLabel = React.forwardRef<
  React.ElementRef<typeof LabelPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof LabelPrimitive.Root>
>(({
    className,
    ...props
}, ref) => {
    const { formItemId } = useFormField();

    return (
        <Label
            htmlFor={formItemId}
            ref={ref}
            className={generateValidClassNameHandler(
                "mb-2 text-foreground font-medium",
                className,
            )}
            {...props}
        />
    );
});
FormLabel.displayName = "FormLabel";

const FormControl = React.forwardRef<
  React.ElementRef<typeof Slot>,
  React.ComponentPropsWithoutRef<typeof Slot>
>(({ ...props }, ref) => {
    const {
        error,
        formDescriptionId,
        formItemId,
        formMessageId,
    } = useFormField();

    return (
        <Slot
            aria-invalid={!!error}
            id={formItemId}
            ref={ref}
            aria-describedby={
                !error
                    ? `${formDescriptionId}`
                    : `${formDescriptionId} ${formMessageId}`
            }
            {...props}
        />
    );
});
FormControl.displayName = "FormControl";

const FormDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({
    className,
    ...props
}, ref) => {
    const { formDescriptionId } = useFormField();

    return (
        <p
            id={formDescriptionId}
            ref={ref}
            className={generateValidClassNameHandler(
                "text-xs text-gray-500",
                className,
            )}
            {...props}
        />
    );
});
FormDescription.displayName = "FormDescription";

const FormMessage = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({
    children,
    className,
    ...props
}, ref) => {
    const {
        error,
        formMessageId,
    } = useFormField();
    const body = error ? String(error?.message) : children;

    if (!body) return null;

    return (
        <p
            id={formMessageId}
            ref={ref}
            className={generateValidClassNameHandler(
                "text-xs md:text-sm text-danger",
                className,
            )}
            {...props}
        >
            {body}
        </p>
    );
});
FormMessage.displayName = "FormMessage";

export {
    Form,
    FormControl,
    FormDescription,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
    useFormField,
};
